/**
 * TEMPORARY COMPONENT - DELETE AFTER USER MIGRATION COMPLETE
 * Purpose: Account migration page for transitioning from Azure AD to Entra External ID
 * Created: 2025-10-15
 * Expected sunset: After all users migrated from Teams synthetic emails to personal emails
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { MuiOtpInput } from 'mui-one-time-password-input';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import npsfLogoWhite from '../assets/npsf-logo-white.png';
import { sendOTP, verifyOTP, migrateAccount } from '../api/athena4p-api';
import '../styles/RequestForm.css';

type Severity = 'error' | 'success' | 'info' | 'warning' | undefined;

const ResetPassword: React.FC = () => {
  const { instance } = useMsal();

  // Form fields
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userId, setUserId] = useState('');

  // OTP states
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpStatusMessage, setOtpStatusMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<Severity>('info');

  // Error and loading states
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Redirect to /athena after migration is complete
  useEffect(() => {
    if (migrationComplete) {
      const countdownInterval = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            window.location.href = '/athena';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [migrationComplete]);

  useEffect(() => {
    const account = instance.getActiveAccount();
    if (account) {
      const email =
        (account.idTokenClaims?.email as string) || account.username || '';
      setCurrentEmail(email);

      const accountUserId =
        (account.idTokenClaims?.oid as string) || account.localAccountId || '';
      setUserId(accountUserId);
    }
  }, [instance]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setNewEmail(email);

    // Validate domain in real-time
    if (email) {
      const domain = email.split('@')[1]?.toLowerCase();
      if (domain === 'athena4p.org' || domain === 'npsalumni.org') {
        setEmailError(true);
      } else {
        setEmailError(false);
      }
    } else {
      setEmailError(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!currentEmail) {
      setError('Current account email is required');
      return;
    }

    if (!newEmail) {
      setError('New personal email is required');
      return;
    }

    // Validate new email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check if email domain is invalid (already validated in real-time, but double-check on submit)
    if (emailError) {
      setError(
        'Please use a personal email address. Athena4p.org and npsalumni.org emails cannot be used as your new login email.'
      );
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword.length > 256) {
      setError('Password must be 256 characters or less');
      return;
    }

    // Entra External ID requires 3 out of 4: uppercase, lowercase, numbers, symbols
    const hasLowercase = /[a-z]/.test(newPassword);
    const hasUppercase = /[A-Z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSymbol = /[^a-zA-Z0-9]/.test(newPassword);
    const complexityCount = [
      hasLowercase,
      hasUppercase,
      hasNumber,
      hasSymbol,
    ].filter(Boolean).length;

    if (complexityCount < 3) {
      setError(
        'Password must contain at least 3 of the following: uppercase letters, lowercase letters, numbers, symbols'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Send OTP to new email
    await sendOtpToUser();
  };

  const sendOtpToUser = async () => {
    setIsSubmitting(true);
    const sendOTPData = {
      email: newEmail,
    };

    try {
      const sendOTPResponse = await sendOTP(sendOTPData);
      if (sendOTPResponse && sendOTPResponse.status === 200) {
        setAlertSeverity('success');
        setOtpStatusMessage(
          `Verification code sent to ${newEmail}. If not received, please check your SPAM folder before resending.`
        );
        setShowOtpDialog(true);
      }
    } catch (err) {
      setAlertSeverity('error');
      setOtpStatusMessage(`Failed to send verification code to ${newEmail}`);
      setError('Failed to send verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    await sendOtpToUser();
  };

  const handleOtpChange = (newValue: string) => {
    setOtp(newValue);
  };

  const handleDialogClose = () => {
    setShowOtpDialog(false);
    setOtp('');
    setOtpStatusMessage('');
  };

  const handleVerifyOtp = async () => {
    setIsVerifying(true);
    const verifyOTPData = {
      email: newEmail,
      otp: otp,
    };

    try {
      const verifyOTPResponse = await verifyOTP(verifyOTPData);
      if (verifyOTPResponse && verifyOTPResponse.status === 200) {
        setOtpStatusMessage('Successfully verified! Completing migration...');
        setAlertSeverity('success');

        // Call account migration API to complete the migration
        try {
          const migrationResponse = await migrateAccount(
            {
              CurrentEmail: currentEmail,
              NewEmail: newEmail,
              Password: newPassword,
            },
            userId
          );

          if (migrationResponse && migrationResponse.status === 200) {
            setMigrationComplete(true);
            setShowOtpDialog(false);
          }
        } catch (migrationErr: any) {
          setAlertSeverity('error');
          setOtpStatusMessage(
            migrationErr.response?.data?.error ||
              'Migration failed. Please contact support at athena@npsfoundation.org'
          );
          return;
        }
      }
    } catch (err: any) {
      setAlertSeverity('error');
      if (err.response) {
        setOtpStatusMessage(
          err.response.data ?? 'Invalid verification code. Please try again.'
        );
      } else {
        setOtpStatusMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  if (migrationComplete) {
    return (
      <div id='request-form-root'>
        <div className='blue-section'>
          <Link
            to='/'
            style={{ display: 'inline-block', marginBottom: '1rem' }}
          >
            <img
              src={npsfLogoWhite}
              alt='NPSF Logo'
              style={{ width: '150px', height: 'auto', display: 'block' }}
            />
          </Link>
          <h1>Migration Successful!</h1>
          <p>Your Athena account has been migrated successfully</p>
        </div>
        <div className='form-wrapper'>
          <Paper elevation={3} sx={{ padding: '2rem', marginTop: '2rem' }}>
            <Stack spacing={3} alignItems='center'>
              <CheckCircleIcon sx={{ fontSize: 80, color: '#4caf50' }} />
              <Typography variant='h5' component='h2' textAlign='center'>
                Account Migration Complete
              </Typography>
              <Typography
                variant='body1'
                color='text.secondary'
                textAlign='center'
              >
                Your account has been successfully migrated to{' '}
                <strong>{newEmail}</strong>. You can now sign in to Athena using
                your new email address and password.
              </Typography>

              <Divider sx={{ width: '100%', my: 2 }} />

              <Typography
                variant='body1'
                color='text.secondary'
                textAlign='center'
              >
                Redirecting to download page in {redirectCountdown} seconds...
              </Typography>

              <Button
                variant='contained'
                size='large'
                href='/athena'
                sx={{
                  backgroundColor: '#002855',
                  '&:hover': {
                    backgroundColor: '#003366',
                  },
                  minWidth: 200,
                }}
              >
                Go to Downloads Now
              </Button>

              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  width: '100%',
                }}
              >
                <Typography
                  variant='body2'
                  color='text.secondary'
                  textAlign='center'
                >
                  Need help? Contact:{' '}
                  <a
                    href='mailto:athena@npsfoundation.org'
                    style={{
                      color: '#002855',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    athena@npsfoundation.org
                  </a>
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <div id='request-form-root'>
      <div className='blue-section'>
        <Link to='/' style={{ display: 'inline-block', marginBottom: '1rem' }}>
          <img
            src={npsfLogoWhite}
            alt='NPSF Logo'
            style={{ width: '150px', height: 'auto', display: 'block' }}
          />
        </Link>
        <h1>Migrate Your Athena Account</h1>
        <p>
          Transition from your Teams account to sign in with your personal email
          address
        </p>
      </div>
      <div className='form-wrapper'>
        <Paper elevation={3} sx={{ padding: '2rem', marginTop: '2rem' }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Typography variant='h5' component='h2'>
                Account Migration
              </Typography>

              <Typography variant='body2' color='text.secondary'>
                Now you can log into Athena with the email address of your
                choice. <br />
                The athena4p.org email will no longer be supported.
              </Typography>

              {error && <Alert severity='error'>{error}</Alert>}

              {/* Current Teams Account */}
              <Alert severity='info' sx={{ width: '100%' }}>
                <Typography variant='body1'>
                  <strong>Your current Teams login:</strong>{' '}
                  {currentEmail || 'Not provided'}
                </Typography>
                <Typography variant='body2' sx={{ mt: 0.5 }}>
                  This will be replaced with your personal email
                </Typography>
              </Alert>

              {!currentEmail && (
                <TextField
                  label='Current Athena Email'
                  type='email'
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  required
                  fullWidth
                  helperText='Enter your athena4p.org or npsalumni.org email to transition to your email of choice'
                />
              )}

              {/* New Personal Email */}

              <TextField
                label='Personal Email Address'
                type='email'
                value={newEmail}
                onChange={handleEmailChange}
                required
                fullWidth
                placeholder='your.email@example.com'
                error={emailError}
                helperText={
                  emailError
                    ? 'Cannot use athena4p.org or npsalumni.org domains. Please use a personal email address.'
                    : 'Please enter your preferred login email address'
                }
              />

              {/* Data Preservation Notice */}
              <Alert severity='info' sx={{ width: '100%' }}>
                <Typography variant='body2'>
                  By signing in with your preferred email, all of your current
                  preferences, information, collections, queries, etc. will be
                  maintained and moved to the new account.
                </Typography>
              </Alert>

              <Divider sx={{ my: 2 }} />

              {/* Password Section */}
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Create a new password
              </Typography>

              <TextField
                label='New Password'
                type='password'
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                fullWidth
                helperText='8-256 characters with at least 3 of: uppercase, lowercase, numbers, symbols'
              />

              <TextField
                label='Confirm Password'
                type='password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
              />

              {/* Support Section */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  Don't have access to an email account?{' '}
                  <a
                    href='mailto:athena@npsfoundation.org'
                    style={{
                      color: '#002855',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                  >
                    Contact athena@npsfoundation.org
                  </a>{' '}
                  for assistance.
                </Typography>
              </Box>

              {/* Submit Button */}
              <Button
                type='submit'
                variant='contained'
                size='large'
                startIcon={<AccountCircleIcon />}
                disabled={isSubmitting}
                sx={{
                  backgroundColor: '#002855',
                  '&:hover': {
                    backgroundColor: '#003366',
                  },
                }}
              >
                {isSubmitting
                  ? 'Sending Verification Code...'
                  : 'Migrate Account'}
              </Button>
            </Stack>
          </form>
        </Paper>
      </div>

      {/* OTP Verification Dialog */}
      <Dialog open={showOtpDialog} maxWidth='sm' fullWidth>
        <Box display='flex' alignItems='center'>
          <Box flexGrow={1}>
            <DialogTitle align='center'>Verify Your Email</DialogTitle>
          </Box>
          <Box>
            <IconButton onClick={handleDialogClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <DialogContent>
          <Stack spacing={3}>
            <Typography>
              We've sent a verification code to <strong>{newEmail}</strong>.
              Please enter it below to complete your account migration.
            </Typography>
            <MuiOtpInput
              value={otp}
              onChange={handleOtpChange}
              length={6}
              autoFocus
            />
            {otpStatusMessage && (
              <Alert severity={alertSeverity}>{otpStatusMessage}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResendOtp}
            color='secondary'
            disabled={isSubmitting}
          >
            Resend Code
          </Button>
          <Button
            onClick={handleVerifyOtp}
            color='primary'
            variant='contained'
            disabled={isVerifying || otp.length !== 6}
          >
            {isVerifying ? 'Verifying...' : 'Verify & Complete Migration'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResetPassword;
